import { DOCUMENT_KIND, DOCUMENT_SIDEBAR_KIND, DOCUMENT_DISPLAY_TYPE } from "@/config/const";

import { AiseaApiFactory } from "@/api/aisea/apiFactory";
import { storageDocuments, db } from "@/firebase/firebaseConfig";
import { PrintUtil, DownloadUtil } from "@/utils";
import moment from "moment";
import _ from "lodash";

const documentApi = AiseaApiFactory.get("enterpriseDocument");
const THUMBNAIL_FOLDER_NAME = ".thumbs";

export default {
  //サイドバーの選択しているアイテムを変更する
  async setSidebarSelected ({ commit }, payload) {
    commit("RESET_CURRENT_PATH")
    commit("RESET_SELECTED_DOCUMENTS")
    commit("RESET_DOCUMENT_SEARCH")
    if (Object.prototype.hasOwnProperty.call(payload, "showFolder")) commit("SET_SHOW_FOLDER_LIST", payload.showFolder)
    if (Object.prototype.hasOwnProperty.call(payload, "showPath")) commit("SET_SHOW_LIST_CARD_PATH", payload.showPath)
    if (Object.prototype.hasOwnProperty.call(payload, "selected")) commit("SET_SELECTED_SIDEBAR_ITEM", payload.selected)
  },

  async selectCard ({ commit }, payload) {
    commit("SET_SELECTED_SIDEBAR_ITEM", payload.path);
    await commit("SET_CURRENT_PATH", payload.pathItems);
    await commit("RESET_SELECTED_DOCUMENTS");
    commit("RESET_DOCUMENT_SEARCH");
  },

  async resetViewState ({ commit }) {
    commit("RESET_SELECTED_DOCUMENTS")
    commit("RESET_DOCUMENT_SEARCH")
  },

  async reloadDocuments ({ state, dispatch, commit }) {
    commit("RESET_DOCUMENTS");

    await dispatch("getStorageCapacity");
    switch (state.selectedSidebarItem) {
      case DOCUMENT_SIDEBAR_KIND.IMPORTANT:
        await dispatch("fetchImportantDocuments")
        break;
      case DOCUMENT_SIDEBAR_KIND.LATEST:
        await dispatch("fetchLatestDocuments")
        break;
      case DOCUMENT_KIND.IMAGE:
      case DOCUMENT_KIND.VIDEO:
      case DOCUMENT_KIND.PDF:
      case DOCUMENT_KIND.WORD:
      case DOCUMENT_KIND.EXCEL:
      case DOCUMENT_KIND.POWERPOINT:
      case DOCUMENT_KIND.OTHER:
        await dispatch("fetchDocumentsKind", state.selectedSidebarItem)
        break;
      default:
        await dispatch("fetchCurrentDocuments")
    }

  },

  //全件検索
  async queryAllDocuments ({ state, commit }, query) {
    try {
      const payload = {
        name: query,
        "search_directory": state.documentRootPath
      }
      const res = await documentApi.search(payload);

      if (!res || !res.status) {
        console.error(res);
        throw res
      }

      const resultFiles = res.data.files.map(item => {
        return {
          id: item.id,
          name: item.name,
          size: item.size,
          path: item.full_path,
          created: item.time_created,
          updated: item.updated,
          type: item.content_type,
          selected: false,
          important: item.custom_metadata.important
        }
      });

      const resultFolders = res.data.folders.map(item => {
        return {
          name: item.name,
          path: item.full_path.replace(/\/$/, ""),
          selected: false
        }
      });

      commit("SET_DOCUMENTS", {
        folders: resultFolders,
        files: resultFiles
      })

    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  //指定されたフォルダの検索
  async queryCurrentDocuments ({ state, commit, dispatch }, query) {
    try {
      await dispatch("reloadDocuments");

      if (query.length === 0) {
        return;
      }

      const resultFiles = state.documents.files.filter(item => {
        return item.name.includes(query);
      });
      const resultFolders = state.documents.folders.filter(item => {
        return item.name.includes(query);
      });

      commit("SET_DOCUMENTS", {
        folders: resultFolders,
        files: resultFiles
      })

    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  //カレントディレクトリのパス取得
  async getCurrentPath ({ state }) {
    try {
      const rootPath = state.documentRootPath;
      let optionPath = "";

      if (state.currentPath.length > 0) {
        const optionStr = state.currentPath.map(item => item.name).join("/");
        optionPath += `${optionStr}/`
      }

      const currentPath = `${rootPath}${optionPath}`;
      return currentPath;

    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  //カレントディレクトリのファイル・フォルダを取得
  async fetchCurrentDocuments ({ commit, dispatch }) {
    try {
      const documentPath = await dispatch("getCurrentPath");
      const ref = storageDocuments.child(documentPath)
      const documents = {};
      const list = await ref.listAll()

      if (list.prefixes && list.prefixes.length > 0) {
        documents.folders = list.prefixes
          .filter(item => item.name !== THUMBNAIL_FOLDER_NAME)
          .map(item => {
            return {
              name: item.name,
              path: item.fullPath,
              selected: false
            }
          })
      } else {
        documents.folders = []
      }

      const promiseAll = list.items
        .map(async item => {
          const path = `${documentPath}/${item.name}/`;
          const ref = storageDocuments.child(path);

          const metadata = await ref.getMetadata()
            .catch(e => {
              throw e;
            });

          const important = metadata.customMetadata ? metadata.customMetadata.important === "true" : false;

          return {
            id: metadata.id,
            name: metadata.name,
            size: metadata.size,
            path: metadata.fullPath,
            created: metadata.timeCreated,
            updated: metadata.updated,
            type: metadata.contentType,
            selected: false,
            important: important
          }
        });

      documents.files = await Promise.all(promiseAll);
      commit("SET_DOCUMENTS", documents);

    } catch (error) {
      console.error(error);
      throw error;
    }
  },


  //ツリーのルートのフォルダ一覧を取得
  async fetchRootFolders ({ state }) {
    try {
      const ref = storageDocuments.child(state.documentRootPath)
      const res = await ref.listAll();

      if (!res.prefixes) {
        return [];
      }

      const folders = res.prefixes
        .filter(item => item.name !== THUMBNAIL_FOLDER_NAME)
        .map(item => {
          return {
            name: item.name,
            path: item.fullPath,
            isOpen: false,
            folders: []
          }
        })

      return folders;

    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  async fetchRootSidebarFolders ({ dispatch, commit }) {
    const folders = await dispatch("fetchRootFolders");
    commit("SET_FOLDERS", folders);
  },

  async fetchRootMoveDialogFolders ({ dispatch, commit }) {
    const folders = await dispatch("fetchRootFolders");
    commit("SET_MOVE_DIALOG_FOLDERS", folders);
  },

  //ツリーのフォルダを取得
  async fetchTreeFolders ({ commit }, folder) {
    const res = await storageDocuments.child(folder.path).listAll()
    if (!res.prefixes) return;
    const childFolders = res.prefixes
      .filter(item => item.name !== THUMBNAIL_FOLDER_NAME)
      .map(item => {
        return {
          name: item.name,
          path: item.fullPath,
          isOpen: false,
          folders: []
        }
      })
    const payload = {
      parent: folder,
      childFolders: childFolders
    };
    commit("SET_CHILD_FOLDERS", payload)
  },

  // 重要書類へ追加/解除
  async setImportantFile ({ dispatch }, payload) {
    try {
      const ref = storageDocuments.child(payload.path);
      const metadata = {
        customMetadata: {
          "important": payload.important
        }
      };

      await ref.updateMetadata(metadata);
      await dispatch("resetViewState");
      await dispatch("reloadDocuments");
    } catch (error) {
      console.error(error)
      throw error;
    }
  },

  //重要書類一覧取得
  async fetchImportantDocuments ({ state, commit }) {
    const path = state.documentRootPath;

    const payload = {
      "search_directory": path
    }
    const res = await documentApi.important(payload);

    if (!res.status) {
      console.error(res);
      throw res
    }

    const documents = {
      files: [],
      folders: []
    };

    documents.files = res.data.files.map(item => {

      const important = item.custom_metadata.important ? item.custom_metadata.important === "true" : false;

      return {
        id: item.id,
        name: item.name,
        size: item.size,
        path: item.full_path,
        created: item.time_created,
        updated: item.updated,
        type: item.content_type,
        selected: false,
        important: important
      }
    });

    commit("SET_DOCUMENTS", documents);
  },

  //最新更新書類一覧取得
  async fetchLatestDocuments ({ state, commit }) {
    const path = state.documentRootPath;
    const payload = {
      "search_directory": path
    }
    const res = await documentApi.latest(payload);

    if (!res || !res.status) {
      console.error(res);
      throw res
    }

    const documents = {
      files: [],
      folders: []
    };

    documents.files = res.data.files
      .map(item => {

        const important = item.custom_metadata.important ? item.custom_metadata.important === "true" : false;

        return {
          id: item.id,
          name: item.name,
          size: item.size,
          path: item.full_path,
          created: item.time_created,
          updated: moment(item.updated),
          type: item.content_type,
          selected: false,
          important: important
        }
      })
      .sort((a, b) => {
        if (a.updated > b.updated) return -1;
        if (a.updated < b.updated) return 1;
        return 0;
      });

    commit("SET_DOCUMENTS", documents);

    if (!res.status) {
      console.error(res);
      throw res
    }
  },

  //種類別書類一覧取得
  async fetchDocumentsKind ({ state, commit }, kind) {
    try {
      const ref = db.collection(`enterprise_documents`)
        .doc(`e:${state.enterpriseId}`)
        .collection("vessels")
        .doc(`v:${state.vesselId}`)
        .collection(kind);

      const res = await ref.get().then(doc => {
        if (doc.empty) return []
        return doc.docs.map(item => item.id);
      });

      const promiseAll = res.map(async item => {
        return await ref
          .doc(item)
          .get().then(doc => {
            if (doc.empty) return

            const important = doc.data().important ? doc.data().important === "true" : false;

            return {
              id: doc.data().id,
              name: doc.data().name,
              size: doc.data().size,
              path: doc.data().path,
              created: doc.data().created_at.seconds * 1000,
              updated: doc.data().updated_at.seconds * 1000,
              type: doc.data().content_type,
              important: important,
              selected: false,
            };
          });
      });
      const files = await Promise.all(promiseAll);
      const documents = {
        folders: [],
        files: files
      };
      commit("SET_DOCUMENTS", documents);
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  //ストレージ情報取得
  async getStorageCapacity ({ state, commit }) {
    try {
      const path = state.documentRootPath;
      const payload = {
        "directory": path
      }
      const res = await documentApi.storageCapacity(payload);
      if (!res || !res.status) throw res
      commit("SET_STORAGE_STATUS", res.data)
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  //書類URL取得
  async getFileUrl (_, filePath) {
    try {
      const url = await storageDocuments.child(filePath).getDownloadURL()
      return url;
    } catch (error) {
      return false;
    }
  },

  //ファイルダウンロード
  async downloadFile ({ dispatch }, documents) {
    try {
      const url = await dispatch("getFileUrl", documents.path)
      const blob = await (await fetch(url)).blob()
      DownloadUtil.saveURLs(blob, documents.name)
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  //ファイルプリント
  async printFile ({ dispatch }, documents) {
    try {
      const url = await dispatch("getFileUrl", documents.path)
      const xhr = new XMLHttpRequest();
      xhr.responseType = 'blob';
      xhr.onload = () => {
        const blob = xhr.response;
        const reader = new FileReader();
        reader.onload = e => {
          if (documents.type === "application/pdf") {
            PrintUtil.printPdf(e.target.result)
          } else {
            PrintUtil.printImage(e.target.result)
          }
        }
        reader.readAsDataURL(blob);
      };
      xhr.open('GET', url);
      xhr.send();
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  //ファイルアップロード
  async addDocument ({ commit, dispatch }, payload) {
    try {
      const documentPath = `${await dispatch("getCurrentPath")}${payload.name}`;
      const metadata = {
        customMetadata: {
          important: false
        }
      };

      const ref = storageDocuments.child(documentPath);
      await ref.put(payload.file, metadata).on('state_changed',
        (snapshot) => {
          const status = {
            totalBytes: snapshot.totalBytes,
            transferredBytes: snapshot.bytesTransferred
          }
          commit("SET_UPLOAD_STATUS", status);
        },
        (error) => {
          payload.errorFunc()
          throw error;
        },
        async () => {
          await payload.completedFunc();
          await dispatch("resetViewState");
          await dispatch("reloadDocuments");
          await dispatch("fetchRootSidebarFolders");
        }
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  //ファイル移動
  async moveFile ({ dispatch }, payload) {
    try {

      await documentApi.moveFile(payload).then(res => {
        if (!res || !res.status) {
          throw res
        } else {
          return res.data
        }
      })

      await dispatch("resetViewState");
      await dispatch("reloadDocuments");
      await dispatch("fetchRootSidebarFolders");
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  //フォルダ移動
  async moveFolder ({ dispatch }, payload) {
    try {

      await documentApi.moveFolder(payload).then(res => {
        if (!res || !res.status) {
          throw res
        } else {
          return res.data
        }
      });

      await dispatch("resetViewState");
      await dispatch("reloadDocuments");
      await dispatch("fetchRootSidebarFolders");
    } catch (error) {
      console.error(error);
      throw error;
    }

  },

  //ファイルコピー
  async copyFile ({ dispatch }, payload) {
    try {

      const res = await documentApi.copyFile(payload);
      if (!res.status) {
        throw res
      }

      await dispatch("resetViewState");
      await dispatch("reloadDocuments");
      await dispatch("fetchRootSidebarFolders");
    } catch (error) {
      console.error(error);
      throw error;
    }

  },

  //フォルダコピー
  async copyFolder ({ dispatch }, payload) {
    try {

      //コピーAPIを呼び出す
      const res = await documentApi.copyFolder(payload);
      if (!res.status) {
        throw res
      }

      await dispatch("resetViewState");
      await dispatch("reloadDocuments");
      await dispatch("fetchRootSidebarFolders");
    } catch (error) {
      console.error(error);
      throw error;
    }

  },

  //ファイル名変更
  async renameFile ({ dispatch }, payload) {
    try {
      const destPath = `${await dispatch("getCurrentPath")}${payload.destName}`;

      const param = {
        source_file: payload.sourcePath,
        destination_file: destPath
      };

      await documentApi.renameFile(param).then(res => {
        if (!res || !res.status) {
          throw res
        } else {
          return res.data
        }
      })

      await dispatch("resetViewState");
      await dispatch("reloadDocuments");
      await dispatch("fetchRootSidebarFolders");
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  //ファイル削除
  async deleteFile ({ dispatch }, path) {
    try {
      const ref = storageDocuments.child(path);
      await ref.delete();
      await dispatch("resetViewState");
      await dispatch("reloadDocuments");
      await dispatch("fetchRootSidebarFolders");
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  // ファイル存在チェック
  async existsFile (_, path) {
    try {
      const ref = storageDocuments.child(path);
      const exists = await ref.getMetadata()
        .then(_ => { return true })
        .catch(_ => { return false });
      return exists
    } catch (error) {
      console.error(error);
      throw error
    }
  },

  //フォルダ存在チェック
  async existsFolder (_, payload) {
    try {
      const ref = storageDocuments.child(payload.path);
      const exists = await ref.listAll()
      if (exists.prefixes.length === 0) return;
      return exists.prefixes.findIndex(item => item.fullPath === `${payload.path}${payload.name}`) !== -1
    } catch (error) {
      console.error(error);
    }
  },

  //フォルダ作成
  async addFolder ({ state, dispatch }, payload) {
    try {
      const path = `${state.documentRootPath}${payload.path}`;
      const _payload = {
        "folder_path": path
      }
      const res = await documentApi.addFolder(_payload)

      if (!res.status) {
        throw res
      }

      if (payload.isProcessingDialog) {
        await dispatch("fetchRootMoveDialogFolders");
        return;
      }
      await dispatch("resetViewState");
      await dispatch("reloadDocuments");
      await dispatch("fetchRootSidebarFolders");
      await dispatch("fetchRootMoveDialogFolders");

    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  //フォルダ名変更
  async renameFolder ({ dispatch }, payload) {
    try {
      const destPath = `${await dispatch("getCurrentPath")}${payload.destName}/`;
      const param = {
        source_folder: `${payload.sourcePath}/`,
        destination_folder: destPath
      };

      await documentApi.renameFolder(param).then(res => {
        if (!res || !res.status) {
          throw res
        } else {
          return res.data
        }
      })
      await dispatch("resetViewState");
      await dispatch("reloadDocuments");
      await dispatch("fetchRootSidebarFolders");
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  //フォルダ削除
  async deleteFolder ({ dispatch }, path) {
    try {
      const payload = {
        "folder_path": `${path}/`
      };

      const res = await documentApi.deleteFolder(payload);
      if (!res.status) {
        console.error(res);
        throw res
      }

      await dispatch("resetViewState");
      await dispatch("reloadDocuments");
      await dispatch("fetchRootSidebarFolders");

    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  // ソート
  // payload: {
  //   isAsc: boolean,
  //   key: string
  // }
  sortDocument ({ state, commit }, payload) {

    const _documents = _.cloneDeep(state.documents);

    const isAsc = payload.isAsc ? -1 : 1;
    const key = payload.key;

    _documents.files.sort((a, b) => {
      if (a[key] > b[key]) return -1 * isAsc;
      if (a[key] < b[key]) return 1 * isAsc;
    });

    //ソート条件が名前以外の場合はソートしない
    payload.key !== "name" ?
      _documents.folders :
      _documents.folders.sort((a, b) => {
        if (a.name > b.name) return -1 * isAsc;
        if (a.name < b.name) return 1 * isAsc;
        return 0;
      });

    commit("SET_DOCUMENTS", _documents);
  },


  /*=========================================================================================
   初期化
  ==========================================================================================*/
  async resetEnterpriseDocumentManagerState ({ commit }) {
    let defaultState = {
      documentRootPath: null,
      vesselName: null,
      isDocumentSidebarActive: true,
      isDocumentSidebarClickNotClose: true,
      isContentInfoSidebarActive: false,
      selectedContentViewStyle: DOCUMENT_DISPLAY_TYPE.GRID,
      documents: {
        files: [],
        folders: []
      },
      folders: [],
      moveDialogFolders: [],
      documentSearchQuery: null,
      currentPath: [],
      isSelectedAllFile: false,
      selectedDocument: null,
      selectedDocuments: [],
      documentNewPath: null,
      isSearching: false,
      selectedSidebarItem: null,
      showFolderList: true,
      searchDocumentsRange: "current",
      selectedMoveDialogFolderPath: "",
      showListCardPath: false,
      selectedSidebarText: null,
      storageMaxCapacity: null,
      storageUsage: null,
    };
    commit("RESET_STATE", defaultState);
  },

};
